import { httpsCallable } from 'firebase/functions';
import {
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { requireFirebase } from '../services/firebase';
import type { CustomSpaceModule, Space, SpaceType } from '../types/models';
import { createClientDisplayId } from '../utils/ids';

function isPermissionDenied(
  error: unknown,
): boolean {
  if (
    !error
    || typeof error !== 'object'
    || !('code' in error)
  ) {
    return false;
  }

  const code =
    String(
      (
        error as {
          code?: unknown;
        }
      ).code
      || '',
    );

  return (
    code === 'permission-denied'
    || code === 'firestore/permission-denied'
  );
}


const repairedOwnedSpaceUsers =
  new Set<string>();

const ownedSpaceRepairRequests =
  new Map<
    string,
    Promise<void>
  >();

async function ensureOwnedSpaceMembershipsRepaired(
  requestedUid?: string,
): Promise<void> {
  const {
    auth,
    functions,
  } = requireFirebase();

  const uid =
    requestedUid
    || auth.currentUser?.uid
    || '';

  if (
    !uid
    || auth.currentUser?.uid
      !== uid
    || repairedOwnedSpaceUsers.has(
      uid,
    )
  ) {
    return;
  }

  const existingRequest =
    ownedSpaceRepairRequests.get(
      uid,
    );

  if (existingRequest) {
    await existingRequest;
    return;
  }

  const repair =
    httpsCallable<
      Record<string, never>,
      {
        checked: number;
        repaired: number;
      }
    >(
      functions,
      'repairOwnedSpaceMemberships',
    );

  const request =
    repair({})
      .then(() => {
        repairedOwnedSpaceUsers.add(
          uid,
        );
      })
      .catch((error) => {
        /*
         * Do not make every Space screen unavailable
         * if the repair callable is temporarily offline.
         * Existing valid memberships can still load.
         */
        console.warn(
          '[BajetBN Space membership repair] '
          + 'Unable to verify legacy owner memberships.',
          error,
        );
      })
      .finally(() => {
        ownedSpaceRepairRequests.delete(
          uid,
        );
      });

  ownedSpaceRepairRequests.set(
    uid,
    request,
  );

  await request;
}

export async function listSpaces(uid: string): Promise<Space[]> {
  await ensureOwnedSpaceMembershipsRepaired(
    uid,
  );

  const { db } = requireFirebase();

  const memberSnapshot =
    await getDocs(
      query(
        collection(db, 'spaceMembers'),
        where('uid', '==', uid),
      ),
    );

  const spaceIds =
    Array.from(
      new Set(
        memberSnapshot.docs
          .filter(
            (item) =>
              !item.data().status
              || item.data().status === 'active',
          )
          .map(
            (item) =>
              item.data().spaceId as string,
          )
          .filter(Boolean),
      ),
    );

  const spaces: Space[] = [];

  for (
    let index = 0;
    index < spaceIds.length;
    index += 10
  ) {
    const chunk =
      spaceIds.slice(
        index,
        index + 10,
      );

    if (!chunk.length) {
      continue;
    }

    try {
      const snapshot =
        await getDocs(
          query(
            collection(db, 'spaces'),
            where(
              documentId(),
              'in',
              chunk,
            ),
          ),
        );

      snapshot.forEach(
        (item) =>
          spaces.push({
            id: item.id,
            ...item.data(),
          } as Space),
      );
    } catch (error) {
      if (!isPermissionDenied(error)) {
        throw error;
      }

      /*
       * Firestore rejects an entire IN query when even one
       * requested Space is not readable. Retry individually so
       * a private/internal Space does not hide every other Space.
       */
      for (const spaceId of chunk) {
        try {
          const snapshot =
            await getDoc(
              doc(
                db,
                'spaces',
                spaceId,
              ),
            );

          if (snapshot.exists()) {
            spaces.push({
              id: snapshot.id,
              ...snapshot.data(),
            } as Space);
          }
        } catch (spaceError) {
          if (
            isPermissionDenied(
              spaceError,
            )
          ) {
            continue;
          }

          throw spaceError;
        }
      }
    }
  }

  return spaces.sort(
    (a, b) =>
      a.name.localeCompare(
        b.name,
      ),
  );
}

/**
 * Load one Space directly.
 *
 * Firestore security rules already require the signed-in user to be an
 * active member of the requested Space, so this avoids loading every Space
 * only to locate the current one.
 */
export async function getSpace(spaceId: string): Promise<Space | null> {
  await ensureOwnedSpaceMembershipsRepaired();

  const { db } = requireFirebase();
  const snapshot = await getDoc(doc(db, 'spaces', spaceId));

  return snapshot.exists()
    ? ({ id: snapshot.id, ...snapshot.data() } as Space)
    : null;
}

export async function createSpace(input: {
  uid: string;
  name: string;
  type: Exclude<SpaceType, 'personal'>;
  currency: string;
  timezone: string;
  description?: string;
  customModules?: CustomSpaceModule[];
}): Promise<string> {
  const { functions } = requireFirebase();

  const call = httpsCallable<
    {
      name: string;
      type: Exclude<SpaceType, 'personal'>;
      currency: string;
      timezone: string;
      description?: string;
      customModules?: CustomSpaceModule[];
    },
    { spaceId: string }
  >(
    functions,
    'createSpaceWithEntitlement',
  );

  const result = await call({
    name: input.name,
    type: input.type,
    currency: input.currency,
    timezone: input.timezone,
    description: input.description,
    customModules: input.customModules,
  });

  return result.data.spaceId;
}
export async function updateSpace(spaceId: string, updates: { name: string; description?: string; customModules?: CustomSpaceModule[] }) {
  const { db } = requireFirebase();
  await updateDoc(doc(db, 'spaces', spaceId), {
    name: updates.name.trim(),
    description: updates.description?.trim() || '',
    ...(updates.customModules ? { customModules: updates.customModules } : {}),
    updatedAt: serverTimestamp(),
  });
}

export async function prepareAdbnTechIntegration(
  spaceId: string,
): Promise<void> {
  const { db } = requireFirebase();

  await updateDoc(
    doc(
      db,
      'spaces',
      spaceId,
    ),
    {
      externalIntegrationProvider:
        'adbn_tech',
      externalIntegrationRole:
        'business',
      externalIntegrationStatus:
        'prepared',
      externalIntegrationKey:
        'adbntech',
      updatedAt:
        serverTimestamp(),
    },
  );
}

export async function markAdbnTechIntegrationConnected(
  spaceId: string,
): Promise<void> {
  const { db } = requireFirebase();

  await updateDoc(
    doc(db, 'spaces', spaceId),
    {
      externalIntegrationProvider: 'adbn_tech',
      externalIntegrationRole: 'business',
      externalIntegrationStatus: 'connected',
      externalIntegrationKey: 'adbntech',
      updatedAt: serverTimestamp(),
    },
  );
}

export async function archiveSpace(spaceId: string) {
  const { db } = requireFirebase();
  await updateDoc(doc(db, 'spaces', spaceId), {
    archivedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}


export async function setAdbnTechPaymentAutoSync(
  spaceId: string,
  input: {
    enabled: boolean;
    cutoffIso?: string;
  },
): Promise<void> {
  const { db } = requireFirebase();

  const cutoffIso =
    input.cutoffIso?.trim()
    || '';

  if (
    input.enabled
    && (
      !cutoffIso
      || !Number.isFinite(
        Date.parse(cutoffIso),
      )
    )
  ) {
    throw new Error(
      'A valid ADBN TECH payment auto-sync cutoff is required.',
    );
  }

  await updateDoc(
    doc(db, 'spaces', spaceId),
    {
      externalIntegrationPaymentAutoSyncEnabled:
        input.enabled,
      ...(cutoffIso
        ? {
            externalIntegrationPaymentAutoSyncCutoffIso:
              cutoffIso,
          }
        : {}),
      updatedAt:
        serverTimestamp(),
    },
  );
}

export async function setAdbnTechExpenseAutoSync(
  spaceId: string,
  input: {
    enabled: boolean;
    cutoffIso?: string;
  },
): Promise<void> {
  const { db } = requireFirebase();

  const cutoffIso =
    input.cutoffIso?.trim()
    || '';

  if (
    input.enabled
    && (
      !cutoffIso
      || !Number.isFinite(
        Date.parse(cutoffIso),
      )
    )
  ) {
    throw new Error(
      'A valid ADBN TECH expense auto-sync cutoff is required.',
    );
  }

  await updateDoc(
    doc(db, 'spaces', spaceId),
    {
      externalIntegrationExpenseAutoSyncEnabled:
        input.enabled,
      ...(cutoffIso
        ? {
            externalIntegrationExpenseAutoSyncCutoffIso:
              cutoffIso,
          }
        : {}),
      updatedAt:
        serverTimestamp(),
    },
  );
}

export async function setAdbnTechAccountMappings(
  spaceId: string,
  mappings: Record<string, string>,
): Promise<void> {
  const { db } = requireFirebase();

  const cleanMappings = Object.fromEntries(
    Object.entries(mappings)
      .map(([sourceId, accountId]) => [
        sourceId.trim(),
        accountId.trim(),
      ])
      .filter(
        ([sourceId, accountId]) =>
          Boolean(sourceId)
          && Boolean(accountId),
      ),
  );

  await updateDoc(
    doc(db, 'spaces', spaceId),
    {
      externalIntegrationAccountMappings:
        cleanMappings,
      updatedAt: serverTimestamp(),
    },
  );
}
